import { useState, useEffect } from 'react';
import { getExistingFilterDataFirstValue } from '../../utils';
import { css } from '@linaria/core';
import Button from '../Button';
import FormFieldset from '../FormFieldset';
import FormLegend from '../FormLegend';
import FormTextInput from '../FormTextInput';
import FormSelect from '../FormSelect';
import FormHint from '../FormHint';
import FormField from '../FormField';
import FormLabel from '../FormLabel';
import FormError from '../FormError';
import CheckIcon from '../../icons/Check';

const fieldset = css`
  margin-bottom: 20px;
  border-bottom: solid 1px #cccccc;
`;

const field = css`
  margin-bottom: 0;

  ${FormLegend} {
    margin-bottom: 5px;
  }
`;

const childFields = css`
  display: flex;
  gap: 20px;

  ${FormField} {
    margin-bottom: 10px;
  }

  ${FormLabel} {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    margin-bottom: 5px;
  }
`;

const applyButton = css`
  margin: 10px 0 20px;
  width: 100%;
`;

export default function YearRangeFilters(props) {
  const fromFieldId = props.fromFieldId;
  const toFieldId = props.toFieldId;
  const filters = props.filters;

  const [fromDate, setFromDate] = useState({
    year: '',
    era: 'AD',
  });
  const [toDate, setToDate] = useState({
    year: '',
    era: 'AD',
  });

  const [errors, setErrors] = useState({ from: null, to: null });

  useEffect(() => {
    let existingFromValue = getExistingFilterDataFirstValue(
      fromFieldId,
      filters,
    );
    let existingToValue = getExistingFilterDataFirstValue(toFieldId, filters);

    const yearToPeriod = year => {
      return !year || year > 0 ? 'AD' : 'BC';
    };

    const existingFromPeriod = yearToPeriod(existingFromValue);
    const existingToPeriod = yearToPeriod(existingToValue);

    existingFromValue = existingFromValue ? Math.abs(existingFromValue) : '';
    existingToValue = existingToValue ? Math.abs(existingToValue) : '';

    if (
      existingFromValue.toString() !== fromDate.year ||
      existingFromPeriod !== fromDate.era
    ) {
      setFromDate({
        year: existingFromValue ? existingFromValue.toString() : '',
        era: existingFromPeriod,
      });
    }
    if (
      existingToValue.toString() !== toDate.year ||
      existingToPeriod !== toDate.era
    ) {
      setToDate({
        year: existingToValue ? existingToValue.toString() : '',
        era: existingToPeriod,
      });
    }
  }, [filters]);

  const handleApply = () => {
    // This is called to set the filter's year from 10 BC to -10
    const transformValue = (year, period) => {
      if (!year || year.trim().length < 1) {
        return undefined;
      }
      const yearAsNumber = Number(year);
      return period === 'BC' ? -Math.abs(yearAsNumber) : yearAsNumber;
    };

    const fromPeriodValue = fromDate.era;
    const fromValue = transformValue(fromDate.year, fromPeriodValue);

    const toPeriodValue = toDate.era;
    const toValue = transformValue(toDate.year, toPeriodValue);

    // Validate that 'from' must be before 'to' if both are provided
    if (
      fromValue !== undefined &&
      toValue !== undefined &&
      fromValue > toValue
    ) {
      setErrors({
        from: `The 'from' year must be before the 'to' year`,
        to: null,
      });
      return;
    }
    setErrors({ from: null, to: null });

    if (!fromValue && !toValue) {
      props.removeFilter(fromFieldId);
      props.removeFilter(toFieldId);
      return;
    }

    if (fromValue) {
      props.setFilter(fromFieldId, fromValue);
    } else {
      props.removeFilter(fromFieldId);
    }
    if (toValue) {
      props.setFilter(toFieldId, toValue);
    } else {
      props.removeFilter(toFieldId);
    }
  };

  const handleYearChange = setter => e => {
    const value = e.target.value;

    if (value === '' || /^\d+$/.test(value)) {
      setter(prev => ({ ...prev, year: value }));
    }
  };

  return (
    <FormFieldset
      aria-describedby={props.formHint ? `${props.id}-hint` : undefined}
      className={fieldset}
    >
      <FormLegend hasHint={props.formHint}>{props.label}</FormLegend>

      {props.formHint && (
        <FormHint id={`${props.id}-hint`}>{props.formHint}</FormHint>
      )}

      <FormFieldset className={field} hasError={!!errors.from}>
        <FormLegend>From:</FormLegend>
        {errors.from && <FormError>{errors.from}</FormError>}

        <div className={childFields}>
          <FormField>
            <FormLabel htmlFor={`${props.id}-from-year`}>Year</FormLabel>
            <FormTextInput
              id={`${props.id}-from-year`}
              inputMode="numeric"
              label="Year"
              onChange={handleYearChange(setFromDate)}
              pattern="\d*"
              value={fromDate.year}
            />
          </FormField>
          <FormSelect
            id={`${props.id}-from-era`}
            isClearable={false}
            isSearchable={false}
            label="Era"
            onChange={option => setFromDate({ ...fromDate, era: option.value })}
            options={[
              {
                label: 'AD',
                value: 'AD',
              },
              {
                label: 'BC',
                value: 'BC',
              },
            ]}
            value={fromDate.era}
          />
        </div>
      </FormFieldset>
      <FormFieldset className={field} hasError={!!errors.to}>
        <FormLegend>To:</FormLegend>
        {errors.to && <FormError>{errors.to}</FormError>}

        <div className={childFields}>
          <FormField>
            <FormLabel htmlFor={`${props.id}-to-year`}>Year</FormLabel>
            <FormTextInput
              id={`${props.id}-to-year`}
              inputMode="numeric"
              label="Year"
              onChange={handleYearChange(setToDate)}
              value={toDate.year}
            />
          </FormField>
          <FormSelect
            id={`${props.id}-to-era`}
            isClearable={false}
            isSearchable={false}
            label="Era"
            onChange={option => setToDate({ ...toDate, era: option.value })}
            options={[
              {
                label: 'AD',
                value: 'AD',
              },
              {
                label: 'BC',
                value: 'BC',
              },
            ]}
            value={toDate.era}
          />
        </div>
      </FormFieldset>
      <Button
        StartIcon={CheckIcon}
        className={applyButton}
        disabled={!fromDate.year && !toDate.year}
        onClick={handleApply}
        size="small"
        variant="neutral"
      >
        Apply date range
      </Button>
    </FormFieldset>
  );
}
